import { useEffect, useRef } from "react";
import * as fabric from "fabric";

/**
 * Read-only mini preview of an ads project canvas.
 * Renders canvas_json into a Fabric canvas sized to fit the given container width,
 * preserving the source aspect ratio.
 */
export default function CanvasThumb({ canvasJson, width, height, maxWidth = 320 }) {
  const ref = useRef(null);
  const fabricRef = useRef(null);

  useEffect(() => {
    if (!ref.current || !canvasJson || !width || !height) return;
    try { fabricRef.current?.dispose(); } catch {}
    const c = new fabric.StaticCanvas(ref.current, {
      width, height,
      backgroundColor: canvasJson.background || "#ffffff",
      selection: false,
      interactive: false,
      renderOnAddRemove: false,
    });
    fabricRef.current = c;
    (canvasJson.objects || []).forEach((o) => {
      const obj = build(o);
      if (obj) c.add(obj);
    });
    c.renderAll();
    // Scale the wrapper element down so the thumb fits the card.
    const scale = Math.min(maxWidth / width, 1);
    if (c.wrapperEl) {
      c.wrapperEl.style.transform = `scale(${scale})`;
      c.wrapperEl.style.transformOrigin = "top left";
      c.wrapperEl.style.width = `${width}px`;
      c.wrapperEl.style.height = `${height}px`;
    }
    return () => { try { c.dispose(); } catch {} };
  }, [canvasJson, width, height, maxWidth]);

  const scale = Math.min(maxWidth / (width || 1), 1);
  return (
    <div
      style={{
        width: Math.round((width || 0) * scale),
        height: Math.round((height || 0) * scale),
        overflow: "hidden",
        background: canvasJson?.background || "#fff",
        borderRadius: 8,
      }}
    >
      <canvas ref={ref} />
    </div>
  );
}

function build(o) {
  if (!o || !o.type) return null;
  if (o.type === "rect") {
    return new fabric.Rect({
      left: o.left || 0, top: o.top || 0,
      width: o.width || 100, height: o.height || 100,
      fill: o.fill || "#000", opacity: o.opacity ?? 1,
      rx: o.rx || 0, ry: o.ry || 0, angle: o.angle || 0,
      selectable: false,
    });
  }
  if (o.type === "circle") {
    return new fabric.Circle({
      left: o.left || 0, top: o.top || 0,
      radius: o.radius || 50, fill: o.fill || "#fff",
      opacity: o.opacity ?? 1, selectable: false,
    });
  }
  if (o.type === "textbox" || o.type === "i-text" || o.type === "text") {
    return new fabric.Textbox(o.text || "Text", {
      left: o.left || 0, top: o.top || 0,
      width: o.width || 300,
      fontSize: o.fontSize || 24,
      fontFamily: o.fontFamily || "Inter",
      fontWeight: o.fontWeight || "normal",
      fontStyle: o.fontStyle || "normal",
      fill: o.fill || "#000",
      opacity: o.opacity ?? 1,
      textAlign: o.textAlign || "left",
      lineHeight: o.lineHeight || 1.16,
      selectable: false,
    });
  }
  return null;
}
